import * as React from 'react';

import {AlertBox} from '../alert-box/alert-box';
import {Button} from '../button/button';
import {SaveButton} from '../button/save-button/save-button';
import {ErrorMessage} from '../form/error-message';
import {portal} from '../portal/portal';
import {Header} from '../typography/header';
import {Subheader} from '../typography/subheader';

import styles from './confirmation-dialog.module.css';

type Props = {
    title?: string;
    message: string | React.ReactElement<any>;
    confirmText?: string | React.ReactNode;
    cancelText?: string;
    onCancel: (e?: React.SyntheticEvent<any>) => void;
    onConfirm?: (e: React.SyntheticEvent<any>) => void;
    isConfirmDestructive?: boolean;
    isSaving?: boolean;
    customSaveText?: {
        initial: string;
        saving: string;
        complete: string;
    };
    errorMessage?: string;
    autoFocusCancelButton?: boolean;
};

function ConfirmationDialogComponent(props: Props) {
    // Destructure and assigning a default works better (in general, but also for Flow) than using defaultProps
    const {
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        isConfirmDestructive = true,
        autoFocusCancelButton = true,
    } = props;

    const cancelButtonRef = React.useRef(undefined);

    React.useLayoutEffect(() => {
        if (cancelButtonRef && cancelButtonRef.current) {
            // @ts-expect-error
            cancelButtonRef.current.focus();
        }
    }, []);

    const customSaveText = props.customSaveText ? props.customSaveText : {};

    let confirmButton;
    if (props.onConfirm && typeof confirmText === 'string') {
        confirmButton = (
            <SaveButton
                type='button'
                variant={isConfirmDestructive ? 'danger' : 'primary'}
                // @ts-expect-error
                onClick={props.onConfirm}
                isSaving={Boolean(props.isSaving)}
                // @ts-expect-error
                buttonText={customSaveText.initial || confirmText}
                // @ts-expect-error
                savingText={customSaveText.saving}
                // @ts-expect-error
                successText={customSaveText.complete}
                disabled={Boolean(props.errorMessage)}
            />
        );
    } else if (confirmText && typeof confirmText !== 'string') {
        // Confirm text is a custom component
        confirmButton = confirmText;
    }

    return (
        <AlertBox onClose={props.onCancel}>
            <div className={styles['confirm-box']}>
                {props.errorMessage && (
                    <div className='pb-8'>
                        <ErrorMessage>{props.errorMessage}</ErrorMessage>
                    </div>
                )}
                <div className='mb-16'>
                    {props.title && <Header>{props.title}</Header>}
                    <Subheader>{props.message}</Subheader>
                </div>
                <div className={styles['confirm-buttons']}>
                    {props.onCancel && (
                        <Button
                            variant={isConfirmDestructive ? 'text-primary' : 'text-secondary'}
                            // @ts-expect-error
                            onClick={props.onCancel}
                            getButtonRef={autoFocusCancelButton ? cancelButtonRef : undefined}
                        >
                            {cancelText}
                        </Button>
                    )}
                    {confirmButton}
                </div>
            </div>
        </AlertBox>
    );
}

export const ConfirmationDialog = portal(ConfirmationDialogComponent);
